/**
 * BM Versioning API Client
 *
 * HTTP client for Git-like versioning operations on BM model.
 */

import type { TFeatureId } from "@/shared/types/feature";
import { createFeatureId } from "@/shared/types/feature";
import type {
  ICommitRequest,
  ICommitResponse,
  ICheckoutRequest,
  ICheckoutResponse,
  IFeatureHistory,
  IFeatureCommitInfo,
} from "@/shared/types/bm-versioning";
import { httpClient } from "./client";

// ============================================================================
// API Client Interface
// ============================================================================

export interface IBmVersioningApiClient {
  /**
   * Create a commit from the current HEAD feature.
   * The current feature becomes an archived commit, and a new HEAD is created.
   */
  commit(featureId: TFeatureId, request: ICommitRequest): Promise<ICommitResponse>;

  /**
   * Create a branch from a specific commit.
   * A new HEAD feature is created with BM data copied from the commit.
   */
  checkout(commitFeatureId: TFeatureId, request: ICheckoutRequest): Promise<ICheckoutResponse>;

  /**
   * Get the commit history for a feature.
   * Returns the chain of commits from HEAD to the initial commit.
   */
  getHistory(featureId: TFeatureId): Promise<IFeatureHistory>;
}

// ============================================================================
// API Response Types (internal, for validation)
// ============================================================================

interface ICommitInfoResponse {
  readonly featureId: string | number;
  readonly name: string;
  readonly message: string | null;
  readonly committedBy: number | null;
  readonly committedByName: string | null;
  readonly committedAt: string | null;
  readonly parentFeatureId: string | number | null;
  readonly isHead: boolean;
  readonly projectId: string | number;
}

interface ICommitResponseRaw {
  readonly newFeatureId: string | number;
  readonly commitInfo: ICommitInfoResponse;
}

interface ICheckoutResponseRaw {
  readonly newFeatureId: string | number;
  readonly newFeatureName: string;
}

interface IFeatureHistoryRaw {
  readonly headFeatureId: string | number;
  readonly commits: readonly ICommitInfoResponse[];
}

// ============================================================================
// Normalization Functions
// ============================================================================

function normalizeId(value: unknown): string {
  if (typeof value === "string") return value;
  if (typeof value === "number") return String(value);
  throw new Error(`Invalid id type: ${typeof value}`);
}

function normalizeCommitInfo(data: ICommitInfoResponse): IFeatureCommitInfo {
  return {
    featureId: createFeatureId(normalizeId(data.featureId)),
    name: data.name,
    message: data.message,
    committedBy: data.committedBy,
    committedByName: data.committedByName,
    committedAt: data.committedAt,
    parentFeatureId:
      data.parentFeatureId !== null
        ? createFeatureId(normalizeId(data.parentFeatureId))
        : null,
    isHead: data.isHead,
    projectId: normalizeId(data.projectId),
  };
}

function normalizeCommitResponse(data: ICommitResponseRaw): ICommitResponse {
  return {
    newFeatureId: createFeatureId(normalizeId(data.newFeatureId)),
    commitInfo: normalizeCommitInfo(data.commitInfo),
  };
}

function normalizeCheckoutResponse(data: ICheckoutResponseRaw): ICheckoutResponse {
  return {
    newFeatureId: createFeatureId(normalizeId(data.newFeatureId)),
    newFeatureName: data.newFeatureName,
  };
}

function normalizeFeatureHistory(data: IFeatureHistoryRaw): IFeatureHistory {
  return {
    headFeatureId: createFeatureId(normalizeId(data.headFeatureId)),
    commits: data.commits.map(normalizeCommitInfo),
  };
}

// ============================================================================
// API Client Implementation
// ============================================================================

export class BmVersioningApiClient implements IBmVersioningApiClient {
  async commit(featureId: TFeatureId, request: ICommitRequest): Promise<ICommitResponse> {
    const response = await httpClient.post<ICommitResponseRaw>(
      `/features/${featureId}/commit`,
      request
    );
    return normalizeCommitResponse(response.data);
  }

  async checkout(commitFeatureId: TFeatureId, request: ICheckoutRequest): Promise<ICheckoutResponse> {
    const response = await httpClient.post<ICheckoutResponseRaw>(
      `/features/${commitFeatureId}/checkout`,
      request
    );
    return normalizeCheckoutResponse(response.data);
  }

  async getHistory(featureId: TFeatureId): Promise<IFeatureHistory> {
    const response = await httpClient.get<IFeatureHistoryRaw>(
      `/features/${featureId}/history`
    );
    return normalizeFeatureHistory(response.data);
  }
}

// ============================================================================
// Singleton Instance
// ============================================================================

let bmVersioningApiClient: BmVersioningApiClient | null = null;

export function getBmVersioningApiClient(): BmVersioningApiClient {
  bmVersioningApiClient ??= new BmVersioningApiClient();
  return bmVersioningApiClient;
}
